import {Circle, Fill, Stroke, Style, Text} from "ol/style";
import {Coordinate} from "ol/coordinate";
import {transform as coordsTransform} from "ol/proj";

export type PointStyleArgs = {radius: number, fillColor: string, strokeColor: string, strokeWidth: number};
export type AreaStyleArgs = {strokeColor: string, strokeWidth: number, fillColor: string};

export function createCoords(position: {longitude: number, latitude: number}): Coordinate {
    return coordsTransform([position.longitude, position.latitude], 'EPSG:4326', 'EPSG:3857');
}

export function buildPointStyle(args: PointStyleArgs, progress: number): Style {
    const radius = args.radius + 2 * progress;
    const strokeWidth = args.strokeWidth + 1 * progress;
    return new Style({
        image: new Circle({
            radius,
            fill: new Fill({color: args.fillColor}),
            stroke: new Stroke({color: args.strokeColor, width: strokeWidth}),
        }),
        zIndex: progress > 0.01 ? 4 : 2,
    });
}

export function buildAreaStyle(args: AreaStyleArgs, progress: number): Style {
    return new Style({
        stroke: new Stroke({
            color: args.strokeColor,
            width: args.strokeWidth + 1 * progress,
        }),
        fill: new Fill({
            color: brightenFill(args.fillColor, progress),
        }),
        zIndex: progress > 0.01 ? 3 : 1,
    });
}

/**
 * Increase the alpha of an `rgba(...)` color by `0.15 * progress`.
 * Non-rgba colors are returned unchanged.
 */
function brightenFill(color: string, progress: number): string {
    const match = color.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/);
    if (!match) {
        return color;
    }
    const [r, g, b, a] = [match[1], match[2], match[3], parseFloat(match[4])];
    return `rgba(${r}, ${g}, ${b}, ${Math.min(1, a + 0.15 * progress)})`;
}

/**
 * Style for a cluster feature: outer halo ring + filled circle with count.
 * `progress` (0..1) interpolates between resting and hover appearance.
 */
export function createClusterStyle(count: number, progress: number = 0): Style[] {
    const baseRadius = Math.min(22, 12 + Math.round(Math.log2(count + 1) * 2));
    const radius = baseRadius + 2 * progress;
    const haloAlpha = 0.18 + 0.1 * progress;
    const ringAlpha = 0.35 + 0.2 * progress;
    const innerStrokeWidth = 2 + 1 * progress;

    return [
        new Style({
            image: new Circle({
                radius: radius + 5,
                fill: new Fill({color: `rgba(220, 53, 69, ${haloAlpha})`}),
                stroke: new Stroke({color: `rgba(220, 53, 69, ${ringAlpha})`, width: 1}),
            }),
            zIndex: 5,
        }),
        new Style({
            image: new Circle({
                radius,
                fill: new Fill({color: '#dc3545'}),
                stroke: new Stroke({color: 'white', width: innerStrokeWidth}),
            }),
            text: new Text({
                text: String(count),
                fill: new Fill({color: '#ffffff'}),
                font: 'bold 12px sans-serif',
            }),
            zIndex: 6,
        }),
    ];
}
